import { useEffect }   from 'react'
import { useAuthStore } from '@store/authStore'
import authService      from '@services/authService'

/**
 * AuthProvider — runs on mount to verify the stored token
 * and hydrate the auth store with the current user profile.
 *
 * Also wires up the global `auth:logout` event dispatched by apiClient
 * when a 401 cannot be recovered.
 */
export function AuthProvider({ children }) {
  const { login, logout, setInitializing } = useAuthStore()

  useEffect(() => {
    const token = localStorage.getItem('agrocast_access_token')

    async function initialize() {
      if (!token) {
        // No token present.  Explicitly call logout() to clear any stale
        // isAuthenticated: true that Zustand persist may have rehydrated.
        // Without this, a user with corrupted/missing token but valid persisted
        // state would be incorrectly treated as authenticated.
        logout()
        setInitializing(false)
        return
      }

      try {
        const user = await authService.getProfile()
        login({
          user,
          accessToken:  token,
          refreshToken: localStorage.getItem('agrocast_refresh_token') ?? '',
        })
      } catch {
        // Token present but session is invalid (user deleted, store wiped, etc.)
        logout()
      } finally {
        setInitializing(false)
      }
    }

    initialize()
  }, [login, logout, setInitializing])

  // Listen for forced logout events emitted by apiClient
  useEffect(() => {
    const handleForceLogout = () => logout()
    window.addEventListener('auth:logout', handleForceLogout)
    return () => window.removeEventListener('auth:logout', handleForceLogout)
  }, [logout])

  return children
}
